import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import InputCom from "../../Helpers/InputCom";
import MoneyToPay from "./Payment/MoneyToPay";
import BillInformation from "./Payment/BillInformation";
import CardInformation from "./Payment/CardInformation";
import InvoiceInformation from "./Payment/InvoiceInformation";
import UserInformation from "./Payment/UserInformation";
import {
  setSelectedInvoicesToRedux,
  setCreditBalanceToRedux,
  postAuthCreditCardPayment,
  postMaevnPayment,
  postPaymentConfirmRedux,
} from "../../../actions/paymentAction";
import { getMyAccount } from "../../../actions/userAuthAction";
import PrimaryButton from "../../Helpers/Buttons/PrimaryButton";
import SecondaryButton from "../../Helpers/Buttons/SecondaryButton";

export default function PaymentTab() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // get selectedInvoices, creditBalance from redux
  const selectedInvoices = useSelector(
    (state) => state.setSelectedInvoicesToRedux.selectedInvoices
  );

  const creditBalance = useSelector(
    (state) => state.setCreditBalanceToRedux.creditBalance
  );

  // get companyName, contactEmail from redux
  const myAccountInfo = useSelector((state) => state.getMyAccount);
  const { loading, error, myAccount } = myAccountInfo;
  if (loading) {
    return <div>Loading...</div>;
  }

  // get payment result from redux
  const paymentProcessing = useSelector(
    (state) => state.postCreditCardPayment.processing
  );

  const paymentResult = useSelector(
    (state) => state.postCreditCardPayment.result
  );
  const errorMessage = useSelector(
    (state) => state.postCreditCardPayment.errorMessage
  );

  const companyName = myAccount?.result?.result?.profile?.companyName;
  const userName = myAccount?.result?.result?.profile?.loginId;
  const userId = myAccount?.result?.result?.profile?.userId;
  const contactEmail = myAccount?.result?.result?.profile?.contactEmail;

  const [paymentInfoTmp, setPaymentInfoTmp] = useState({
    userId: userId,
    balance: 0, // Credit Used From Your Balance (USD)
    subTotal: 0, // Subtotal Amount On Invoices (USD) Sum of Balance and Actual amount of paying with credit card
    total: 0, // Total Amount Paying (USD) == Actual amount of paying with credit card
    country: "",
    state: "",
    city: "",
    zipcode: "",
    address1: "",
    address2: "",
    cardHolderName: "",
    cardNumber: "",
    cardExpirationMonth: "",
    cardExpirationYear: "",
    cvv: "",
    selectedInvoice: selectedInvoices,
  });

  // Calculate the sum of subtotal, total for all selected invoices
  const [subtotalPlaceholder, setSubtotalPlaceholder] = useState(0);
  const [totalPlaceholder, setTotalPlaceholder] = useState(0);
  const [creditBalancePlaceHolder, setBalancePlaceHolder] = useState(0);

  // Calculate the sum of balance due for all selected invoices
  useEffect(() => {
    //If even one invoice is not selected, block access to the payment page
    if (selectedInvoices.length === 0) {
      alert("Please select the invoice you want to pay.");

      navigate("/myaccount#invoice");
    }

    // sum of due amount of money for selected invoices
    if (selectedInvoices && Array.isArray(selectedInvoices)) {
      const subtotal = selectedInvoices.reduce(
        (sum, invoice) => sum + (invoice.balanceDue || 0),
        0
      );
      setSubtotalPlaceholder(subtotal.toFixed(2));

      // Total amount of money to pay
      setTotalPlaceholder((prevTotal) => {
        const newTotal =
          parseFloat(prevTotal) + parseFloat(subtotal.toFixed(2));
        return newTotal;
      });

      // Credit balance
      setBalancePlaceHolder(creditBalance);
    }
  }, [selectedInvoices, creditBalance, navigate]);

  // Notes for selected invoices
  const invoicePlaceHolder =
    paymentInfoTmp.selectedInvoice && paymentInfoTmp.selectedInvoice.length > 0
      ? paymentInfoTmp.selectedInvoice.map((invoice) => invoice.num).join(", ")
      : "";

  const [warnings, setWarnings] = useState({});

  // Show warning message for empty required field
  const checkNullAndSetWarnings = (info) => {
    const newWarnings = {};
    for (const [key, value] of Object.entries(info)) {
      if (key === "address2") continue;
      if (key === "balance") continue;

      newWarnings[key] = !value ? "*This field is required." : "";
    }
    setWarnings(newWarnings);

    // Verify that all alerts are empty strings
    if (Object.values(newWarnings).every((warning) => !warning)) {
      return true; // all required fields are filled
    }
    return false; // Missing field
  };

  // Transfer expiration date type
  const createExpirationDate = (paymentInfoTmp) => {
    return `${paymentInfoTmp.cardExpirationYear}-${paymentInfoTmp.cardExpirationMonth}`;
  };

  // Store final bill information
  const transformPaymentInfo = (paymentInfoTmp, expirationDate) => {
    return {
      billAddress: {
        ...paymentInfoTmp,
      },
      creditCard: {
        cardHolderName: paymentInfoTmp.cardHolderName,
        cardNumber: paymentInfoTmp.cardNumber,
        expirationDate: expirationDate,
        cvv: paymentInfoTmp.cvv,
      },
      moneyToPay: paymentInfoTmp,
      selectedInvoice: paymentInfoTmp.selectedInvoice,
      username: paymentInfoTmp.username, // Add username here
    };
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Map updatedPaymentInfo to Authorize.net API call format
  const createAuthorizeAPIRequest = (
    updatedPaymentInfo,
    invoicePlaceHolder
  ) => {
    console.log("updatedPaymentInfo", updatedPaymentInfo);

    console.log("invoicePlaceHolder", invoicePlaceHolder);
    return {
      createTransactionRequest: {
        merchantAuthentication: {
          name: "7e7YQMmrr7L", // Actual merchant name
          transactionKey: "832LqS3pS3T2dDsE", // Actual transaction key

          //name: "5KP3u95bQpv", // Sandbox merchant name
          //transactionKey: "346HZ32z3fP4hTG2", // Sandbox transaction key
        },
        transactionRequest: {
          transactionType: "authCaptureTransaction",
          amount: updatedPaymentInfo.moneyToPay.total, // get total price from updatedPaymentInfo
          payment: {
            creditCard: {
              cardNumber: updatedPaymentInfo.creditCard.cardNumber,
              expirationDate: updatedPaymentInfo.creditCard.expirationDate,
              cardCode: updatedPaymentInfo.creditCard.cvv,
            },
          },
          customer: {
            id: updatedPaymentInfo.billAddress.userId,
          },
          billTo: {
            firstName: updatedPaymentInfo.billAddress.cardHolderName,
            lastName: "",
            address: updatedPaymentInfo.billAddress.address1,
            city: updatedPaymentInfo.billAddress.city,
            state: updatedPaymentInfo.billAddress.state,
            zip: String(updatedPaymentInfo.billAddress.zipcode),
            country: updatedPaymentInfo.billAddress.country,
          },

          userFields: {
            userField: [
              {
                name: "invoiceNumber",
                value: invoicePlaceHolder, // get selected invoice number from updatedPaymentInfo
              },
              {
                name: "creditAmount",
                value: updatedPaymentInfo.moneyToPay.balance, // get total price from updatedPaymentInfo
              },
              {
                name: "account#",
                value: userName, // get total price from updatedPaymentInfo
              },
            ],
          },
        },
      },
    };
  };

  // Map updatedPaymentInfo to Maevn API call format
  const createMaevnAPIRequest = (
    updatedPaymentInfo,
    invoicePlaceHolderTmp,
    payResult,
    transId,
    errorMsg
  ) => {
    return {
      userId: updatedPaymentInfo.billAddress.userId,
      creditUsed: updatedPaymentInfo.moneyToPay.balance,
      subTotal: updatedPaymentInfo.moneyToPay.subTotal,
      total: updatedPaymentInfo.moneyToPay.total,
      invoices: invoicePlaceHolderTmp,
      creditCard: updatedPaymentInfo.creditCard.cardNumber,
      cardHolderName: updatedPaymentInfo.billAddress.cardHolderName,
      transactionId: transId,
      // Credit card billing address
      country: updatedPaymentInfo.billAddress.country,
      state: updatedPaymentInfo.billAddress.state,
      city: updatedPaymentInfo.billAddress.city,
      zipcode: updatedPaymentInfo.billAddress.zipcode,
      address1: updatedPaymentInfo.billAddress.address1,
      address2: updatedPaymentInfo.billAddress.address2,
      paymentResult: payResult,
      errorMessage: errorMsg,
    };
  };

  const [showPaymentAlert, setShowPaymentAlert] = useState(false);
  const [alertPaymentContent, setPaymentAlertContent] = useState({
    transId: "",
    resultCode: "",
    message: "",
  });

  const handleSubmitButtonClick = async () => {
    if (isSubmitting) return; // ✅ re-entrancy guard
    setIsSubmitting(true);

    try {
      // For calling payment confirmation email depending on the Auth. transaction result
      let paymentResult = 0;

      // Ensure empty string values are treated as 0
      const paymentInfoWithDefaults = {
        ...paymentInfoTmp,
        balance: paymentInfoTmp.balance === "" ? 0 : paymentInfoTmp.balance,
        subTotal: paymentInfoTmp.subTotal === "" ? 0 : paymentInfoTmp.subTotal,
        total: paymentInfoTmp.total === "" ? 0 : paymentInfoTmp.total,
      };

      // Check whether required information is provided
      const allFieldsFilled = checkNullAndSetWarnings(paymentInfoWithDefaults);
      if (!allFieldsFilled) {
        console.log("Some fields are missing");
        return;
      }

      // Create the cardExpirationDate by concatenating cardExpirationYear and cardExpirationMonth
      const expirationDateTmp = createExpirationDate(paymentInfoWithDefaults);

      // Collect datas for Authorize.net & Email receipt sender API
      const updatedPaymentInfo = transformPaymentInfo(
        paymentInfoWithDefaults,
        expirationDateTmp
      );

      const authorizeAPIRequestData = createAuthorizeAPIRequest(
        updatedPaymentInfo,
        invoicePlaceHolder
      );

      console.log("authorizeAPIRequestData", authorizeAPIRequestData);

      // ──────────────────────────────────────────────────────────────
      // Inner try: Authorize.net Call + Conditions
      // ──────────────────────────────────────────────────────────────
      try {
        // 1) Authorize.net API
        const response = await dispatch(
          postAuthCreditCardPayment(authorizeAPIRequestData)
        );

        // 2) Collect a response from Authorize.net
        const tr = response?.data?.transactionResponse;
        if (!tr) {
          alert(
            "Payment gateway returned an unexpected response. Please try again."
          );
          return;
        }

        const transId = tr?.transId ?? null;
        const msg0 = Array.isArray(tr?.messages) ? tr.messages[0] : undefined;
        const err0 = Array.isArray(tr?.errors) ? tr.errors[0] : undefined;

        // Approval deicsion : Use only messages[0].code
        const decisionCode = msg0?.code?.toString?.() || ""; // "1" | "253" | etc
        const successMessage = msg0?.description || "Approved";

        // Error flag
        const errorCode = err0?.errorCode ?? null;
        const errorText =
          err0?.errorText ??
          "The transaction has not been successful. Please review your billing information.";
        const topLevelResult = response?.data?.messages?.resultCode || ""; // "Ok" | "Error"
        const hasErrorFlags = Boolean(errorCode) || topLevelResult === "Error";

        // Decision standard: decisionCode ∈ {"1","253"}
        const isApproved = decisionCode === "1" || decisionCode === "253";

        if (isApproved) {
          // Success
          paymentResult = 1;

          setPaymentAlertContent({
            transId,
            resultCode: decisionCode,
            message: successMessage,
          });

          // Send email receipt
          const maevnAPIRequestData = createMaevnAPIRequest(
            updatedPaymentInfo,
            invoicePlaceHolder,
            paymentResult,
            transId,
            successMessage
          );

          try {
            const maevnResponse = await dispatch(
              postMaevnPayment(maevnAPIRequestData)
            );
            dispatch(postPaymentConfirmRedux(maevnResponse));
          } catch {
            alert(
              `Your payment was approved, but we couldn't send the email receipt.\n` +
                `Please contact our accounting team about your payment.\n` +
                `Transaction ID: ${transId ?? "N/A"}`
            );
            return;
          }

          setShowPaymentAlert(true); // Success alert
          return;
        }

        // Failure standard : (NOT 1/253) AND (errors[0].errorCode  OR top-level resultCode === "Error")
        if (!isApproved && hasErrorFlags) {
          paymentResult = 0;

          const details = [
            errorCode ? `ErrorCode: ${errorCode}` : null,
            decisionCode ? `DecisionCode: ${decisionCode}` : null,
            transId ? `Transaction ID: ${transId}` : null,
          ]
            .filter(Boolean)
            .join(" | ");

          alert(details ? `${errorText}\n${details}` : errorText);
          return;
        }

        // Other responses except 1 or 253
        alert("An unexpected payment state was returned. Please try again.");
        return;
      } catch (e) {
        console.error("[Authorize.Net] Unexpected error:", e);
        alert(
          "An unexpected error occurred while processing your payment. Please try again."
        );
      } finally {
        setIsSubmitting(false);
      }
      // ──────────────────────────────────────────────────────────────
      // End inner try/catch
      // ──────────────────────────────────────────────────────────────
    } catch (outerErr) {
      // Other errors outside of Authorize.net API condition
      console.error("[Payment Submit] Outer error:", outerErr);
      alert(
        "Something went wrong before processing the payment. Please try again."
      );
    } finally {
      setIsSubmitting(false); // ✅ always re-enable the button
    }
  };

  const handleCancelButtonClick = () => {
    navigate("/myaccount#invoice");
  };

  //useEffect(() => {
  //  console.log("paymentInfoTmp in paymentTab : ", paymentInfoTmp);
  //}, [paymentInfoTmp]);

  return (
    <>
      <div className="flex space-x-8 ml-5">
        <div className="w-full ">
          {/* Pay Your Invoice */}
          <div
            className="flex justify-between items-center "
            style={{
              marginBottom: "20px",
              borderBottom: "1px solid lightgray",
            }}
          >
            <div className="flex space-x-3 items-center">
              <span>
                <svg
                  width="30"
                  height="25"
                  viewBox="0 0 17 12"
                  className="fill-current"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fill="#000000"
                    d="M12.7405 0.0107422H3.61054C1.81171 0.0136959 0.352563 1.46989 0.349609 3.27167H15.9985C15.9985 1.47284 14.5394 0.0136959 12.7405 0.0107422Z"
                  />
                  <path
                    fill="#000000"
                    d="M0.349609 8.4879C0.352563 10.2867 1.80876 11.7459 3.61054 11.7488H12.7405C14.5394 11.7459 15.9985 10.2897 16.0015 8.4879V4.57715H0.349609V8.4879ZM4.91314 8.16299C4.91314 8.70352 4.47598 9.14068 3.93545 9.14068C3.39492 9.14068 2.95776 8.70352 2.95776 8.16299C2.95776 7.62245 3.39492 7.1853 3.93545 7.1853C4.47598 7.1853 4.91314 7.62245 4.91314 8.16299Z"
                  />
                </svg>
              </span>
              <span
                className="text-2xl font-bold payment-subtitle"
                style={{ color: "#000000" }}
              >
                Pay Your Invoice
              </span>
            </div>
            {/* move to back button */}
            <div className="w-[100px] h-[40px] mt-4 mb-4">
              <button
                type="button"
                className="text-lg font-semibold"
                onClick={handleCancelButtonClick}
              >
                {"< BACK"}
              </button>
            </div>
          </div>
          <div
            className="flex space-x-3 items-center "
            style={{ marginBottom: "40px" }}
          >
            <span className="text-sm font-semibold">
              Please provide the following information to pay your invoice. When
              you are finished, click "Submit Payment" at the bottom of the page
              to proceed
            </span>
          </div>
          {/* User Information */}
          <UserInformation
            companyName={companyName}
            userName={userName}
            contactEmail={contactEmail}
            paymentInfoTmp={paymentInfoTmp}
            setPaymentInfoTmp={setPaymentInfoTmp}
            warnings={warnings}
          />

          {/* Invoice Information */}
          <InvoiceInformation
            selectedInvoices={selectedInvoices}
            paymentInfoTmp={paymentInfoTmp}
            setPaymentInfoTmp={setPaymentInfoTmp}
            warnings={warnings}
          />

          {/* Input amount of money */}
          <MoneyToPay
            creditBalancePlaceHolder={creditBalancePlaceHolder}
            invoicePlaceHolder={invoicePlaceHolder}
            subtotalPlaceholder={subtotalPlaceholder}
            totalPlaceholder={totalPlaceholder}
            paymentInfoTmp={paymentInfoTmp}
            setPaymentInfoTmp={setPaymentInfoTmp}
            warnings={warnings}
          />

          {/* Bill Information */}
          <BillInformation
            paymentInfoTmp={paymentInfoTmp}
            setPaymentInfoTmp={setPaymentInfoTmp}
            warnings={warnings}
          />

          {/* Card Information */}
          <CardInformation
            paymentInfoTmp={paymentInfoTmp}
            setPaymentInfoTmp={setPaymentInfoTmp}
            warnings={warnings}
          />
        </div>
      </div>

      {/* Buttons */}
      <div className="action-area flex space-x-4 ml-5 items-center">
        {/* <button
          type="button"
          className="text-sm text-qred font-semibold"
          onClick={handleCancelButtonClick}
        >
          Cancel
          </button> */}
        <SecondaryButton
          width="218px"
          height="40px"
          fontSize="18px"
          onClick={handleCancelButtonClick}
          // disabled={disabled}
        >
          Cancel{" "}
        </SecondaryButton>

        {/* <button
          type="button"
          className="w-[164px] h-[50px] bg-qblack text-white text-sm"
          onClick={handleSubmitButtonClick}
        >
          Submit Payment
          </button> */}

        <PrimaryButton
          width="218px"
          height="40px"
          fontSize="18px"
          onClick={handleSubmitButtonClick}
          disabled={isSubmitting}
        >
          Submit Payment
        </PrimaryButton>
      </div>

      {/* Payment approval alert */}
      {showPaymentAlert && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-md w-[400px] text-sm">
            <span className="inline-flex items-center">
              <h3
                style={{ color: "#04ADD1" }}
                className="font-semibold text-lg mb-4"
              >
                Payment Successful!
              </h3>
            </span>
            <p>
              <strong>Transaction ID: </strong> {alertPaymentContent.transId}
            </p>
            <p className="mt-2">
              <strong>Message: </strong>
              {alertPaymentContent.message}
            </p>
            <p className="mt-2">
              <strong>Result Code: </strong> {alertPaymentContent.resultCode}
            </p>
            <div className="text-right mt-4">
              <button
                className="bg-[#04ADD1] hover:bg-[#0399B8] text-white px-8 py-2 rounded"
                onClick={() => {
                  setShowPaymentAlert(false);
                  navigate("/paymentConfirm");
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
